<template>
  <div v-if="columns.length" class="bracket" :style="frame">
    <section v-for="column in columns" :key="column.round" class="round">
      <div class="round-head">
        <h3>{{ column.label }}</h3>
        <TournamentRoundClock v-if="column.endsAt" :ends-at="column.endsAt" />
      </div>
      <div class="pairings">
        <article
          v-for="match in column.matches"
          :key="match.id"
          class="pairing"
          :class="{ live: playing(match) }"
        >
          <div class="match">
            <div class="card">
              <div class="players">
                <button
                  v-for="seat in seats(match)"
                  :key="seat.key"
                  type="button"
                  class="player"
                  :class="{ winner: seat.winner, idle: seat.idle }"
                  :disabled="!pickable || !seat.player"
                  @click="choose(seat.player)"
                >
                  <span class="name">{{ seat.text }}</span>
                  <TournamentResultMark
                    v-if="seat.mark"
                    :kind="seat.mark"
                    :label="markLabel(match, seat.player, seat.mark)"
                  />
                </button>
              </div>
            </div>
            <button
              v-if="match.roomCode && !seated(match)"
              type="button"
              class="watch"
              @click="emit('spectate', match.roomCode)"
            >
              {{ t('tournament.spectate') }}
            </button>
            <button
              v-else-if="seated(match) && (match.status === 'waiting' || match.status === 'live')"
              type="button"
              class="watch play"
              @click="emit('play', match.id)"
            >
              {{ match.status === 'live' ? t('tournament.rejoin') : t('tournament.play') }}
            </button>
            <button
              v-else-if="match.replayId"
              type="button"
              class="watch"
              @click="emit('replay', match.replayId)"
            >
              {{ t('tournament.replay') }}
            </button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { TournamentMatchView, TournamentPlayerView } from '@shared/index'
import TournamentResultMark from './TournamentResultMark.vue'
import TournamentRoundClock from './TournamentRoundClock.vue'
import { useI18n } from '../i18n/t'

type BracketMark = 'play' | 'time' | 'bye' | 'show'

const props = defineProps<{
  matches: TournamentMatchView[]
  me?: string | null
  pickable?: boolean
}>()

const emit = defineEmits<{
  pick: [player: TournamentPlayerView]
  spectate: [code: string]
  play: [matchId: string]
  replay: [replayId: string]
}>()

const { t } = useI18n()

const columns = computed(() => {
  const groups = new Map<number, TournamentMatchView[]>()
  for (const match of filled(props.matches ?? [])) {
    const list = groups.get(match.round) ?? []
    list.push(match)
    groups.set(match.round, list)
  }
  const rounds = [...groups.entries()].sort((a, b) => a[0] - b[0])
  const max = rounds.reduce((best, [round]) => Math.max(best, round), 0)
  return rounds.map(([round, list]) => ({
    round,
    label: roundLabel(max - round),
    endsAt: roundEndsAt(list),
    matches: [...list].sort((a, b) => a.slot - b.slot),
  }))
})

const frame = computed(() => {
  const first = columns.value[0]?.matches.length ?? 1
  return { '--rows': String(Math.max(first, 1)) }
})

function roundEndsAt(list: TournamentMatchView[]): string | null {
  let best = ''
  for (const match of list) {
    if (match.status === 'done' || !match.endsAt) continue
    if (!best || match.endsAt < best) best = match.endsAt
  }
  return best || null
}

function roundLabel(fromEnd: number): string {
  if (fromEnd <= 0) return t('tournament.final')
  if (fromEnd === 1) return t('tournament.semi')
  if (fromEnd === 2) return t('tournament.quarters')
  return t('tournament.eighths')
}

function filled(matches: TournamentMatchView[]): TournamentMatchView[] {
  if (matches.length === 0) return matches
  const firstRound = matches.reduce((best, match) => Math.min(best, match.round), matches[0].round)
  const lastRound = matches.reduce((best, match) => Math.max(best, match.round), firstRound)
  let width = matches.filter((match) => match.round === firstRound).length
  const out = [...matches]
  let round = firstRound
  while (width > 1) {
    width = Math.ceil(width / 2)
    round += 1
    if (round <= lastRound) continue
    for (let slot = 0; slot < width; slot += 1) {
      out.push({
        id: `open-${round}-${slot}`,
        round,
        slot,
        status: 'waiting',
        roomCode: null,
        player0: null,
        player1: null,
        winnerUserId: null,
        bye: false,
        outcome: null,
        endsAt: null,
      })
    }
  }
  const byKey = new Map(out.map((match) => [`${match.round}:${match.slot}`, match]))
  for (const match of out) {
    if (!match.id.startsWith('open-')) continue
    paintOpen(
      match,
      byKey.get(`${match.round - 1}:${match.slot * 2}`),
      byKey.get(`${match.round - 1}:${match.slot * 2 + 1}`),
    )
  }
  return out
}

/** A finished match sends its winner forward. A no-winner leaves the seat empty. */
function winnerPlayer(match: TournamentMatchView | undefined): TournamentPlayerView | null {
  if (!match || match.status !== 'done' || !match.winnerUserId) return null
  if (match.player0?.userId === match.winnerUserId) return match.player0
  if (match.player1?.userId === match.winnerUserId) return match.player1
  return null
}

function paintOpen(
  match: TournamentMatchView,
  left: TournamentMatchView | undefined,
  right: TournamentMatchView | undefined,
): void {
  const leftDone = left?.status === 'done'
  const rightDone = right?.status === 'done'
  if (leftDone) match.player0 = winnerPlayer(left)
  if (rightDone) match.player1 = winnerPlayer(right)
  if (!leftDone || !rightDone || (match.player0 && match.player1)) return
  const solo = match.player0 ?? match.player1
  match.status = 'done'
  match.bye = true
  match.outcome = 'bye'
  match.winnerUserId = solo?.userId ?? null
}

function seats(match: TournamentMatchView) {
  return [match.player0, match.player1].map((player, index) => ({
    key: index,
    player,
    text: label(match, player),
    winner: won(match, player),
    idle: !player,
    mark: seatMark(match, player),
  }))
}

function missed(match: TournamentMatchView, player: TournamentPlayerView | null): boolean {
  if (!player || !match.missedUserIds?.length) return false
  return match.missedUserIds.includes(player.userId)
}

/** Won because the other player did not open the room in the 5 minutes. */
function checkinWin(match: TournamentMatchView, player: TournamentPlayerView): boolean {
  if (match.winnerUserId !== player.userId) return false
  if (match.outcome === 'checkin') return true
  return (
    match.outcome === 'time' &&
    Boolean(match.missedUserIds?.some((id) => id !== player.userId))
  )
}

function seatMark(
  match: TournamentMatchView,
  player: TournamentPlayerView | null,
): BracketMark | null {
  if (match.status !== 'done') return null
  if (player && checkinWin(match, player)) return 'show'
  if (missed(match, player)) return 'time'
  if (player && match.outcome === 'checkin') return 'time'
  if (player && match.winnerUserId === player.userId) {
    if (match.outcome === 'play') return 'play'
    if (match.outcome === 'time') return 'time'
    return null
  }
  if (player && !match.winnerUserId && match.outcome === 'time') return 'time'
  if (!player && (match.bye || match.outcome === 'bye')) return 'bye'
  return null
}

function markLabel(
  match: TournamentMatchView,
  player: TournamentPlayerView | null,
  kind: BracketMark,
): string {
  if (kind === 'show') return t('tournament.markShow')
  if (kind === 'time' && (missed(match, player) || !match.winnerUserId || match.outcome === 'checkin')) {
    return t('tournament.markNoShow')
  }
  if (kind === 'time') return t('tournament.markTime')
  if (kind === 'play') return t('tournament.markPlay')
  return t('tournament.markBye')
}

function label(match: TournamentMatchView, player: TournamentPlayerView | null): string {
  if (player) return player.username
  if (!match.player0 && !match.player1) return '—'
  if (match.status !== 'done') return '—'
  return t('tournament.bye')
}

function won(match: TournamentMatchView, player: TournamentPlayerView | null): boolean {
  return Boolean(player && match.status === 'done' && match.winnerUserId === player.userId)
}

function playing(match: TournamentMatchView): boolean {
  return match.status === 'live' && Boolean(match.player0 || match.player1)
}

function seated(match: TournamentMatchView): boolean {
  if (!props.me) return false
  return match.player0?.userId === props.me || match.player1?.userId === props.me
}

function choose(player: TournamentPlayerView | null): void {
  if (!props.pickable || !player) return
  emit('pick', player)
}
</script>

<style scoped>
.bracket {
  --match: 128px;
  --link: 36px;
  --rule: 2px;
  display: flex;
  align-items: flex-start;
  gap: var(--link);
  width: 100%;
  overflow-x: auto;
  padding: 4px 2px 12px;
}

.round {
  display: flex;
  flex-direction: column;
  width: 200px;
  flex: 0 0 200px;
}

.round-head {
  height: 40px;
  flex: 0 0 40px;
}

.round h3 {
  margin: 0;
  font-size: 12px;
  font-weight: 650;
  line-height: 16px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.round-head .round-clock {
  margin: 2px 0 0;
}

.pairings {
  height: calc(var(--rows) * var(--match));
  display: flex;
  flex-direction: column;
}

.pairing {
  position: relative;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Two matches meet halfway across the gap, then a stub enters the next one. */
.pairing:nth-child(odd)::after,
.pairing:nth-child(even)::after {
  content: '';
  position: absolute;
  left: calc(100% - 1px);
  width: calc(var(--link) / 2 + 1px);
  border-color: var(--md-sys-color-outline);
  border-style: solid;
  border-left: 0;
  pointer-events: none;
}

.pairing:nth-child(odd)::after {
  top: 50%;
  bottom: 0;
  border-width: var(--rule) var(--rule) 0 0;
}

.pairing:nth-child(even)::after {
  top: 0;
  bottom: 50%;
  border-width: 0 var(--rule) var(--rule) 0;
}

.pairing:nth-child(odd)::before {
  content: '';
  position: absolute;
  top: calc(100% - var(--rule) / 2);
  left: calc(100% + var(--link) / 2 - var(--rule));
  width: calc(var(--link) / 2 + var(--rule));
  height: var(--rule);
  background: var(--md-sys-color-outline);
  pointer-events: none;
}

.round:last-child .pairing::before,
.round:last-child .pairing::after,
.pairing:nth-child(odd):last-child::before,
.pairing:nth-child(odd):last-child::after {
  content: none;
}

.match {
  position: relative;
}

.players {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  background: var(--md-sys-color-surface);
  color: var(--md-sys-color-on-surface);
}

.player {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 36px;
  padding: 6px 10px;
  border: 0;
  border-bottom: 1px solid var(--md-sys-color-outline-variant, rgba(255, 255, 255, 0.12));
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.player:last-child {
  border-bottom: 0;
}

.player:disabled {
  opacity: 1;
  cursor: default;
}

.player.winner {
  font-weight: 750;
  background: color-mix(in srgb, var(--md-sys-color-primary) 22%, var(--md-sys-color-surface));
}

.player.idle .name {
  opacity: 0.55;
}

.card {
  border-radius: 10px;
}

.pairing.live .card {
  animation: duel-pulse 1.4s ease-in-out infinite;
}

@keyframes duel-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--md-sys-color-primary) 0%, transparent);
  }
  50% {
    box-shadow: 0 0 0 6px color-mix(in srgb, var(--md-sys-color-primary) 70%, transparent);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pairing.live .card {
    animation: none;
    box-shadow: 0 0 0 2px var(--md-sys-color-primary);
  }
}

.watch {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
}
</style>
